/**
 * A thread started optimistically (contracts §9.1's `thread_id` + `message_id`):
 * Send makes the pair here and opens the thread at once. Until
 * `POST /api/threads` lands, the thread is its start in the outbox: the view
 * renders a placeholder from it (never a fetch, which would be a 404), and
 * the lists show its row. Once it lands, the real thread replaces both under
 * the same ids, title and key, so nothing flickers.
 *
 * Edit on a start that wasn't sent takes it back to the composer that starts
 * threads on that surface (`startDrafts`): the side panel's New thread, or
 * Chats' composer. The thread never existed, so there's nothing to stay on.
 */
import { useCallback, useSyncExternalStore } from "react";
import type { Uploaded } from "../../app/api";
import type { Me } from "../../app/me";
// The Worker's own, so the title doesn't change when the thread lands (slice 1 spec §3.1).
import { threadTitle } from "../../../worker/domain/thread";
import type { ThreadDetail, ThreadItem } from "./api";
import type { Unsent } from "./outbox";

/** The thread a start stands for, empty: the outbox merges its messages in (`unsentLayout`). */
export function placeholderDetail(start: Unsent): ThreadDetail {
  return {
    thread: {
      id: start.thread_id,
      title: threadTitle(start.body),
      started_by_user_id: start.author_user_id,
      created_at: start.created_at,
      last_message_at: start.created_at,
    },
    messages: [],
    has_more: false,
    turns: [],
    tasks: [],
    docs: [],
  };
}

/** A start's row in the thread lists: queued while it's sent, quiet once Not sent. */
export function startItem(start: Unsent, person: Me["user"]): ThreadItem {
  return {
    ...placeholderDetail(start).thread,
    started_by: { id: person.id, name: person.name, initials: person.initials, tint: person.tint },
    preview: start.body,
    reply_count: 0,
    activity: start.state === "failed" ? null : "queued",
    agent: null,
  };
}

type Pages = { pages: Array<{ threads: ThreadItem[] }> };

/**
 * The loaded lists with this person's starts they don't hold yet on top,
 * newest first. The same data when there are none, so nothing rerenders.
 */
export function withStarts<D extends Pages>(data: D, starts: Unsent[], person: Me["user"]): D {
  const loaded = new Set(data.pages.flatMap((p) => p.threads.map((t) => t.id)));
  const extra = starts
    .filter((s) => s.author_user_id === person.id && !loaded.has(s.thread_id))
    .map((s) => startItem(s, person))
    .reverse();
  if (extra.length === 0 || data.pages.length === 0) return data;
  const [first, ...rest] = data.pages;
  return { ...data, pages: [{ ...first!, threads: [...extra, ...first!.threads] }, ...rest] };
}

// The words an Edited start takes back, for the surface's start composer.

export type StartSurface = "chats" | "panel";

/**
 * `n` counts the drafts put, so each remounts the composer once with its
 * words; `doc_id` is its doc, a chip until removed or sent (where there's no
 * Ask to change to carry it); `files` its uploads, back as chips with the
 * words (Codex on #435).
 */
export type StartDraft = {
  n: number;
  body: string | null;
  doc_id: string | null;
  files: Uploaded[];
};

const EMPTY: StartDraft = { n: 0, body: null, doc_id: null, files: [] };
const drafts = new Map<StartSurface, StartDraft>();
const draftListeners = new Set<() => void>();

export const startDrafts = {
  get: (surface: StartSurface) => drafts.get(surface) ?? EMPTY,
  /** An Edited start's words, for the next start composer on `surface`. */
  put(surface: StartSurface, body: string, doc_id: string | null = null, files: Uploaded[] = []) {
    drafts.set(surface, { n: startDrafts.get(surface).n + 1, body, doc_id, files });
    for (const listener of draftListeners) listener();
  },
  /** Logging out: no Edited start's words or doc stay for the next person. */
  clear() {
    if (drafts.size === 0) return;
    // The counts go too: a composer keyed on one remounts empty.
    drafts.clear();
    for (const listener of draftListeners) listener();
  },
  /** Its doc's chip removed, or sent. */
  dropDoc(surface: StartSurface) {
    const draft = startDrafts.get(surface);
    if (draft.doc_id === null) return;
    drafts.set(surface, { ...draft, doc_id: null });
    for (const listener of draftListeners) listener();
  },
  /** The composer has the words: they aren't put again (the count stays, and the key). */
  taken(surface: StartSurface) {
    const draft = startDrafts.get(surface);
    if (draft.body === null) return;
    drafts.set(surface, { ...draft, body: null, files: [] });
    for (const listener of draftListeners) listener();
  },
  subscribe: (listener: () => void) => {
    draftListeners.add(listener);
    return () => void draftListeners.delete(listener);
  },
};

export function useStartDraft(surface: StartSurface | undefined): StartDraft {
  const snapshot = useCallback(() => (surface ? startDrafts.get(surface) : EMPTY), [surface]);
  return useSyncExternalStore(startDrafts.subscribe, snapshot, snapshot);
}
